import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dictionary } from '@/src/lib/dictionary/types';
import { SelectOption } from '@/src/types/select-option';
import { useRouter } from 'next/navigation';
import debounce from 'lodash/debounce';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import LoadingSpinner from '@/src/app/components/LoadingSpinner/LoadingSpinner';
import AppButton from '@/src/app/components/Button/AppButton';
import getCatalogSearchSuggestionsClient from '@/src/lib/catalog-search-suggestions/requests/get-catalog-search-suggestions.client';
import { handleException } from '@/src/lib/core/exception/handle-exception';
import { useNotify } from '@/src/lib/notification/notify';
import { NotificationType } from '@/src/app/components/Notification/types';
import Icon from '@/src/app/components/Icon/Icon';
import { IconName } from '@/src/app/components/Icon/types';
import { transformURLForFrontendPaths } from '@/src/lib/utils/url.ts';
type Props = {
  dict: Dictionary;
  lang: string;
  country: string;
  setOverlay: (open: boolean) => void;
  searchQuery?: string;
};
const SEARCH_MIN_CHARACTERS = 3;
export default function SearchForm({
  lang,
  country,
  dict,
  setOverlay,
  searchQuery
}: Props) {
  const [query, setQuery] = useState(searchQuery || '');
  const [searchSuggestionsLoading, setSearchSuggestionsLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);
  const [options, setOptions] = useState<Array<SelectOption>>([]);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const router = useRouter();
  const {
    toastNotification
  } = useNotify();
  const debouncedSearch = useMemo(
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  () => debounce(async (searchKey: string, lang_0: string) => {
    try {
      setSearchSuggestionsLoading(true);
      const result = await getCatalogSearchSuggestionsClient(searchKey, lang_0);
      if (result.data) {
        const mappedOptions = result.data[0].attributes.completion.map(option => ({
          value: option,
          label: option
        }));
        setOptions(mappedOptions);
      }
      setSearchSuggestionsLoading(false);
    } catch (e) {
      handleException(e);
      toastNotification(dict.general.error, NotificationType.ERROR);
    }
  }, 500), []);
  function getHighlightedOptionLabel(label: string) {
    return label.replace(new RegExp(query, 'gi'), '<strong>$&</strong>');
  }
  function onSubmitForm(event: React.SyntheticEvent, inputValue?: string) {
    event.preventDefault();
    if (query.length < SEARCH_MIN_CHARACTERS) return;
    const searchKey_0 = inputValue ? inputValue : query;
    if (setOverlay) setOverlay(false);
    router.push(transformURLForFrontendPaths(`/search?q=${encodeURIComponent(searchKey_0)}`, lang, country));
  }
  function onKeyDownInput(event_0: React.KeyboardEvent<HTMLInputElement>, inputValue_0?: SelectOption) {
    if (event_0.key === 'Escape' && query.length) {
      event_0.stopPropagation();
      setQuery('');
    }
    if (event_0.key === 'Enter') {
      event_0.preventDefault();
      if (inputValue_0) {
        setQuery(String(inputValue_0.value));
      }
      if (query.length >= SEARCH_MIN_CHARACTERS || inputValue_0 && String(inputValue_0.value).length >= SEARCH_MIN_CHARACTERS) {
        onSubmitForm(event_0, String(inputValue_0?.value || query));
      }
    }
  }
  function onOptionClick(event_1: React.MouseEvent<HTMLDivElement, MouseEvent>, inputValue_1: string) {
    event_1.preventDefault();
    setQuery(inputValue_1);
    onSubmitForm(event_1, inputValue_1);
  }
  function onInputChange(event_2: React.ChangeEvent<HTMLInputElement>) {
    const newValue = event_2.target.value;
    setQuery(newValue);
    if (!selectedOption || newValue !== selectedOption.label) {
      setSelectedOption(newValue ? {
        value: newValue,
        label: newValue
      } : null);
    }
  }
  function onClearButtonClick(event_3: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event_3.preventDefault();
    event_3.stopPropagation();
    setQuery('');
    setSelectedOption(null);
  }
  useEffect(() => {
    (async () => {
      setOptions([]);
      if (query.length >= SEARCH_MIN_CHARACTERS) {
        await debouncedSearch(query, lang);
      }
    })();
  }, [query]);
  return <>
      <h1 className="text-center display-title-3xl lg:tall:-mt-24">{dict.search.title}</h1>
      <form className="flex items-stretch gap-4 mt-8 lg:mt-12" method="GET" action={transformURLForFrontendPaths('/search', lang, country)} data-testid="search-form" onSubmit={onSubmitForm}>
        <Combobox as="div" className="flex-grow" value={selectedOption} onChange={option_0 => {
        setSelectedOption(option_0);
      }} by="label">
          {({
          activeOption
        }) => <>
              <Label className="sr-only">{dict.general.search}</Label>
              <div className="relative">
                <Icon name={IconName.SEARCH} className="absolute left-4 top-1/2 -translate-y-1/2" />
                <ComboboxInput ref={inputRef} className="w-full pl-12" onChange={onInputChange} displayValue={(option_1: SelectOption) => option_1?.label || ''} autoComplete="off" name="q" autoFocus={true} id="overlay-search" placeholder={dict.search.placeholder} onKeyDown={event_4 => onKeyDownInput(event_4, activeOption as SelectOption)} value={query} />
                <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden" />

                {options.length > 0 && <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <ComboboxOptions className="absolute max-h-[15.625rem] w-full overflow-auto bg-white text-base border border-gray-300 focus:outline-hidden mt-2">
                      <ComboboxOption value={{
                  value: null,
                  label: query
                }} className="hidden" />
                      {options.map(option_2 => <ComboboxOption key={option_2.value} value={option_2} className="group cursor-default select-none py-3 pl-3 pr-9 data-[focus]:bg-black data-[focus]:text-white text-gray-900" onClick={event_5 => onOptionClick(event_5, option_2.label)}>
                          <span className="block truncate group-data-[selected]:font-bold" dangerouslySetInnerHTML={{
                    __html: getHighlightedOptionLabel(option_2.label)
                  }} />

                          <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-[selected]:flex group-data-[focus]:text-white">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        </ComboboxOption>)}
                    </ComboboxOptions>
                  </Transition>}
                {searchSuggestionsLoading ? <div className="absolute right-12 top-1/2 -translate-y-1/2">
                    <LoadingSpinner small />
                  </div> : null}
                {query.length ? <button className="absolute right-0 top-0 bottom-0 py-2 px-3 leading-none text-gray-400 focus:outline-hidden" title={dict.search.clear} data-testid="clear-search" type="button" onClick={onClearButtonClick}>
                    <Icon name={IconName.CLOSE} />
                  </button> : null}
              </div>
            </>}
        </Combobox>
        <AppButton type="submit" className="priority" disabled={query.length < SEARCH_MIN_CHARACTERS}>
          {dict.search.submit}
        </AppButton>
      </form>
    </>;
}